/**
 * Intercom Controller - Hide Intercom widget on specific pages
 * 
 * This script checks if the current page is in the exclusion list and hides
 * the Intercom widget accordingly. It uses both CSS and the Intercom API
 * to ensure the widget stays hidden on excluded pages.
 */

(function() {
  // List of page paths where Intercom should be hidden
  const excludedPaths = [
    '/pages/quiz',
    '/pages/recommendation',
    '/cart',
    '/pages/personalised-review',
    '/pages/personalised-library',
    '/pages/personalised-home',
    '/pages/personalised-reminder',
    '/pages/personalised-calendar',
    '/pages/rewards',
    '/products'
  ];
  
  // Check if current page is in the exclusion list
  function shouldHideIntercom() {
    const currentPath = window.location.pathname;
    return excludedPaths.some(path => {
      // Either exact match or starts with path plus /
      return currentPath === path || currentPath.startsWith(path + '/');
    });
  }
  
  // Hide Intercom elements immediately with CSS
  function injectHidingCSS() {
    const style = document.createElement('style');
    style.id = 'intercom-hiding-style';
    style.innerHTML = `
      #intercom-frame,
      .intercom-lightweight-app,
      .intercom-app,
      iframe[src*="intercom"],
      div[class*="intercom"] {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    console.debug('[Intercom Controller] Hiding CSS injected');
  }
  
  // Use Intercom API to shut down the widget if available
  function shutdownIntercomAPI() {
    if (window.Intercom) {
      try {
        window.Intercom('shutdown');
        console.debug('[Intercom Controller] Intercom API shutdown called');
      } catch (e) {
        console.error('[Intercom Controller] Error shutting down Intercom:', e);
      }
    }
  }
  
  // Watch for Intercom elements being added to the DOM
  function setupElementObserver() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for Intercom elements
              if (
                node.id === 'intercom-frame' || 
                node.classList?.contains('intercom-lightweight-app') ||
                node.classList?.contains('intercom-app') ||
                (node.tagName === 'IFRAME' && node.src?.includes('intercom')) ||
                (node.className && typeof node.className === 'string' && node.className.includes('intercom'))
              ) {
                // Hide the element
                node.style.display = 'none';
                node.style.opacity = '0';
                node.style.visibility = 'hidden';
                node.style.pointerEvents = 'none';
                console.debug('[Intercom Controller] Dynamically hidden Intercom element:', node);
              }
              
              // Also check children recursively (for deeper nesting)
              if (node.hasChildNodes()) {
                const intercomElements = node.querySelectorAll('#intercom-frame, .intercom-lightweight-app, .intercom-app, iframe[src*="intercom"], [class*="intercom"]');
                intercomElements.forEach(el => {
                  el.style.display = 'none';
                  el.style.opacity = '0';
                  el.style.visibility = 'hidden';
                  el.style.pointerEvents = 'none';
                });
                
                if (intercomElements.length > 0) {
                  console.debug('[Intercom Controller] Hidden nested Intercom elements:', intercomElements.length);
                }
              }
            }
          }
        }
      }
    });
    
    // Observe the entire document for changes
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    
    console.debug('[Intercom Controller] DOM observer set up');
  }
  
  // Watch for Intercom script/API being loaded
  function setupIntercomAPIWatcher() {
    let intercomChecks = 0;
    const maxChecks = 10;
    
    const checkForIntercom = () => {
      if (window.Intercom && typeof window.Intercom === 'function') {
        shutdownIntercomAPI();
        return true;
      }
      
      intercomChecks++;
      if (intercomChecks < maxChecks) {
        setTimeout(checkForIntercom, 500);
      }
      return false;
    };
    
    // Start checking
    setTimeout(checkForIntercom, 500);
  }
  
  // Initialize the controller
  function init() {
    if (!shouldHideIntercom()) {
      console.debug('[Intercom Controller] Current page not in exclusion list, Intercom will be displayed');
      return;
    }
    
    console.debug('[Intercom Controller] Current page in exclusion list, hiding Intercom widget');
    
    // Apply immediate hiding
    injectHidingCSS();
    
    // Set up watchers for dynamic content
    setupElementObserver();
    setupIntercomAPIWatcher();
    
    // If Intercom is already loaded, shut it down
    if (window.Intercom) {
      shutdownIntercomAPI();
    }
  }
  
  // Run on DOMContentLoaded to ensure early execution
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, run immediately
    init();
  }
})();
